import React, { forwardRef } from "react";
//#region types
import { WintSuperkollAPIModelAnnualAccounts } from "wint-api-client/dist/types";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { Box, Typography } from "@mui/material";
import MultipleAutoCompleteInput from "components/common/forms/baseFields/multipleAutoCompleteInput";
import { useFloatingMessages } from "components/common";
import { useAnnualAccountService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

export interface ManageAssistantsHandle {
  submit: () => Promise<void>;
}

export interface ManageAssistantsContainerProps {
  financialYearId: number;
  availableAuditors: WintSuperkollAPIModelAnnualAccounts.AuditorAssistantOption[];
  initialAssistants: WintSuperkollAPIModelAnnualAccounts.AuditorAssistantOption[];
  onSaved: () => void;
}

const ManageAssistantsContainer: React.ForwardRefRenderFunction<ManageAssistantsHandle, ManageAssistantsContainerProps> = (
  props,
  ref
) => {
  const { updateAuditorAssistants } = useAnnualAccountService();
  const { enqueueSuccessMessage, enqueueErrorMessage } = useFloatingMessages();

  const auditorItems = React.useMemo<AutocompleteListItem[]>(
    () =>
      props.availableAuditors
        .filter((a) => !!a.socialSecurityNumber)
        .map((a) => ({
          id: a.socialSecurityNumber,
          value: a.name ?? a.socialSecurityNumber!,
        })),
    [props.availableAuditors]
  );

  const [selections, setSelections] = React.useState<AutocompleteListItem[]>(() =>
    props.initialAssistants
      .filter((a) => !!a.socialSecurityNumber)
      .map((a) => {
        const match = props.availableAuditors.find((av) => av.socialSecurityNumber === a.socialSecurityNumber);
        return {
          id: a.socialSecurityNumber,
          value: match?.name ?? a.name ?? a.socialSecurityNumber!,
        };
      })
  );

  React.useImperativeHandle(ref, () => ({
    submit: async () => {
      const ssns = selections.map((s) => String(s.id ?? "")).filter((s) => s.length > 0);
      try {
        await updateAuditorAssistants(props.financialYearId, ssns);
        enqueueSuccessMessage(
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.AssistantsSaved)
        );
        props.onSaved();
      } catch (error) {
        enqueueErrorMessage(
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.AssistantsSaveFailed)
        );
        throw error;
      }
    },
  }));

  return (
    <Box sx={{ py: 3 }}>
      <Typography
        component="label"
        htmlFor="auditor-assistants"
        sx={{ display: "block", mb: 1.5, fontFamily: '"MuseoSans-700", sans-serif', fontSize: 16, color: "#000" }}
      >
        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Reports.AuditorAssistants)}
      </Typography>
      <MultipleAutoCompleteInput
        name="auditor-assistants"
        value={selections}
        items={auditorItems}
        suggestedItems={[]}
        onItemsSelect={setSelections}
        placeholder={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Select)}
      />
    </Box>
  );
};

export default forwardRef(ManageAssistantsContainer);
